import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { OrderWrapperComponent } from "../../component/wrapper/order-wrapper.component";
import { OrderOrFulfillment, OrderPublic } from '../../../../globalTypes/order.types';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { Store } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { accountFeature } from '../../store/account.store';
import { debounceTime, filter, lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { fulfillmentPageOrders, FulfillmentPageOrdersVariables, getFulfillment, getFulfillmentOrder, getOrder, sendMessage, SendMessageVariables, startFulfillment, startFulfillmentShipping, StartFulfillmentShippingVariables, StartFulfillmentVariables } from '@dataconnect/account';
import { getFormattedFulfillments, getFormattedOrder, getOrderAllocatedShippingFunds, sleep } from '../../../../globalTypes/_helpers';
import { Logger } from '../../helper/logger';
import { Fulfillment, FulfillmentStatusType } from '../../../../globalTypes/fulfillment.types';
import { FulfillmentOrderEntryComponent } from "./fulfillment-order-entry.component";
import { FulfillmentOrderDetailsComponent } from "./fulfillment-order-details.component";
import { OrderService } from '../../services/order.service';
import { MatDialog } from '@angular/material/dialog';
import { StartShippingData, StartShippingDialogComponent } from '../../component/shipping/start-shipping.dialog';
import { sortByKey } from '../../helper/general';
import { ButtonDisplay } from '../../component/layout/element/button.component';
import { LocationPublic } from '../../../../globalTypes/account.types';
import { Status } from '../../../../globalTypes/base.types';
import { AccountService } from '../../services/account.service';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';
import { AcceptShippingRateDialogComponent, AcceptShippingRateDialogData } from '../../component/shipping/accept-shipping-rate.dialog';
import { FlagInteractionVariables } from '../../../../globalTypes/interactions.types';
import { interactionsFeature } from '../../store/interactions.store';
import { InteractionsService } from '../../services/interactions.service';
import { NotificationHandle } from '../../../../globalTypes/communication.types';
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
import { onCallFunctions } from '../../../../globalTypes/backend.api.types';
import { authUserFeature } from '../../store/authUser.store';
import { AuthService } from '../../services/auth.service';
import { v4 as uuidV4 } from 'uuid';
import { EditProductReviewDialogComponent } from '../../component/review/edit.review.dialog';
import { createBlankReview, formatFulfillmentForReviewObject, ReviewForEdit } from '../../../../globalTypes/review.types';
import { uiErrorCatcher } from '../../helper/error.handling';
import { ViewProductReviewDialogComponent } from '../../component/review/view.review.dialog';
const log = new Logger("FulfillmentComponent")

@Component({
  selector: 'msh-fulfillment',
  imports: [OrderWrapperComponent, FulfillmentOrderEntryComponent, FulfillmentOrderDetailsComponent, LoadingLogoComponent],
  template: `
  <msh-order-wrapper
    [order]="order"
    [fulfillmentStatusMap]="fulfillmentStatusMap"
  >
    <div class=" m-auto flex justify-center ">
      @if(!initComplete){
        <msh-loading-logo class=" w-96 h-96"></msh-loading-logo>
      } @else if(!order){
       @if(!orders || orders.length === 0) { <div class="null">There are no fulfillment needs for this location</div> } @else {
        <div class="w-full mx-0 md:mx-12">
          @for(order of orders; track order.id){
            <msh-fulfillment-order-entry
              [order]="order"
              (onActionTaken)="actionTaken($event)"
              [takingAction]="takingAction"
              [ctaMap]="ctaMap"
              [buyerReviews]="buyerReviews"
              [sellerReviews]="sellerReviews"
              [fulfillmentStatusMap]="fulfillmentStatusMap"
              class="block w-full"
            ></msh-fulfillment-order-entry>
          }
        </div>
       }
      } @else { 
        <msh-fulfillment-order-details 
          [order]="order" 
          [fulfillments]="fulfillments"
          (onActionTaken)="actionTaken($event)" 
          (requestRehydrate)="setPage(true)"
          [buyerReviews]="buyerReviews"
          [sellerReviews]="sellerReviews"
          (onOpenReviewDialog)="openReviewDialog($event)"
          [takingAction]="takingAction" class=" block w-full ml-0 md:ml-20"
          [ctaMap]="ctaMap"
          [fulfillmentStatusMap]="fulfillmentStatusMap"
        ></msh-fulfillment-order-details>
      }
    </div>
  </msh-order-wrapper>  `,
  styles: ``
})
export class FulfillmentComponent extends Base<FulfillmentComponent> implements OnInit, OnDestroy {
  orderService = inject(OrderService);
  
  focusedOrderId?: string
  order?: OrderPublic;
  fulfillments: Fulfillment[] = []
  orders: OrderPublic[] = [];
  location!: LocationPublic;
  locations: {[locationId: string]: LocationPublic} = {}
  fulfillmentStatusMap: {[statusId: string]: Status} = {}
  pageSetup$: Subject<void> = new Subject<void>();
  initComplete = false;
  successMessage = '';
  takingAction = false;
  startedMarkingSeenInteractions = false;
  buyerReviews: { [fulfillmentId: string]: ReviewForEdit } = {};
  sellerReviews: { [fulfillmentId: string]: ReviewForEdit } = {};
  pageSet = false;
  standardCtaOptions = [
    {cta: "Send Message", fn: (ev: MouseEvent, optionData: any) => this.orderService.sendMessageDialog(optionData, false)},
    {cta: "Report Issue", fn: (ev: MouseEvent, optionData: any) => this.orderService.sendMessageDialog(optionData, true) },
    {cta: "Cancel & Refund", fn: (ev: MouseEvent, optionData: any) => this.orderService.startRefund(optionData, false)},
  ]
  ctaMap: {[status in FulfillmentStatusType]?: ButtonDisplay } = {
    required: { cta: "Start Fulfillment", options: [...this.standardCtaOptions]},
    waiting: { cta: "Start Fulfillment!", options: [...this.standardCtaOptions]},
    delinquency: { cta: "Start Fulfillment!", options: [...this.standardCtaOptions]},
    terminated: { cta: "Archive", options: []},
    fulfilling: { cta: "Shipping Details", options: [...this.standardCtaOptions]},
    shipping: { cta: "Shipment Details", stealthMode: true, options: [...this.standardCtaOptions]},
    delayed: { cta: "Cancelled Shipment", stealthMode: true, options: [...this.standardCtaOptions]},
    incident: { cta: "Resume", options: [...this.standardCtaOptions]},
    arrived: { cta: "Write Review", stealthMode: true, options: []},
    downloadable: { cta: "Write Review", options: []},
    review: { cta: "Write Review", options: []},
  }

  constructor(
    private store: Store,
    private functions: Functions,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private accountService: AccountService,
    private authService: AuthService,
    private interactionsService: InteractionsService,
  ){
    super();
    
    console.log("init'n fulfillment component")
    this.store.select(accountFeature.selectLocations).pipe(takeUntil(this.$destroy), filter(l => !!l)).subscribe(async locations => {
      this.locations = locations as {[locationId:string]: LocationPublic}
    })
    this.router.events.pipe(takeUntil(this.$destroy)).subscribe(async(event) => {
      console.log({event})
      if (!(event instanceof NavigationEnd)) return    
      if(this.route.snapshot.params['orderId'] && this.focusedOrderId !== this.route.snapshot.params['orderId']){ 
        this.pageSet = false; 
        this.focusedOrderId = this.route.snapshot.params['orderId']
      }
      if(!this.location) this.location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), take(1))) as LocationPublic;
      console.log({location: this.location})
      await sleep(100);
      const statuses = [...await this.accountService.getStatuses('fulfillments'), ...await this.accountService.getStatuses('refunds')];
      this.fulfillmentStatusMap = statuses.reduce((a,s,i)=>{a[s.id]=s; return a;},{} as {[statusId: string]: Status})
      
      this.setPage();      
    })
    this.store.select(interactionsFeature.selectInteractions).pipe(takeUntil(this.$destroy)).subscribe(interactions => {
      const needsRefreshedContent = interactions.some(i => this.fulfillments.some(f => f.id === i?.fulfillment.id))
      if(needsRefreshedContent){
        this.setPage(true);
      }
    })
  }
  async ngOnInit(): Promise<void> {
    this.setPage();
  }

  async setPage(isRehydrate = false){
    if(this.pageSet && !isRehydrate) return;
    this.pageSet = true;

    if(this.focusedOrderId) {
      this.startedMarkingSeenInteractions = false;
      this.setPageForOneOrder();
    } else {
      this.setPageForManyOrders();
    }
  }

  async setPageForOneOrder(){
    if(!this.focusedOrderId) return;
    if(!this.location) this.location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), take(1))) as LocationPublic;
    const orderId: string = this.focusedOrderId;
    // const orders = await lastValueFrom(this.store.select(ordersFeature.selectOrders).pipe(take(1)))
    // let order = orders.find(order => order && order.id === orderId)
    //if(!order) {
    const user = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(filter(u => !!u), take(1)));
    console.log({user, isUser: !!user});
    await sleep(100);
    const orderRef = await getFulfillmentOrder({orderId, locationId: this.location.id})
    const fulfillments = orderRef.data.fulfillments as unknown as Fulfillment[]
    console.log({place: 'setPageForOneOrder', fulfillments})
    const orderFromFulfillments = fulfillments.sort(sortByKey('fulfillmentNumber')).reduce((a, fulfillment, idx)=>{
      const {id, orderNumber, buyerId, orderPlaced, paymentTransactionId, statusId} = fulfillment.order;
      const thisShippingAmount = fulfillment.rateId ? 0 : fulfillment.rateCost
      const commissionRate = fulfillment.commissionAmount / (fulfillment.fulfillmentSubTotalAmount || 1);
      this.setReviews(fulfillment);
      if(!a[id]){
        const marketPlaceCommission = fulfillment.commissionAmount;
        const totalAmount = fulfillment.fulfillmentFixedShippingAmount + fulfillment.fulfillmentSubTotalAmount - marketPlaceCommission;
        const order: OrderOrFulfillment = {
          id,
          orderNumber,
          paymentTransactionId,
          buyerId,
          discounts_on_order: fulfillment.discounts_on_fulfillment,
          orderPlaced,
          fulfillments_on_order: [],
          orderItems_on_order: [],
          subTotalAmount: fulfillment.fulfillmentSubTotalAmount,
          totalTaxAmount: fulfillment.fulfillmentSalesTaxAmount,
          totalAmount,
          totalDiscountAmount: fulfillment.fulfillmentDiscountAmount,
          totalShippingAmount: thisShippingAmount,
          totalFixedShippingAmount: fulfillment.fulfillmentFixedShippingAmount,
          isFulfillment: true,
          marketPlaceCommission,
          statusId
        } 
        a[id] = order;
      }else{
        a[id].subTotalAmount = (a[id].subTotalAmount || 0) + fulfillment.fulfillmentSubTotalAmount
        a[id].totalTaxAmount = (a[id].totalTaxAmount || 0) + fulfillment.fulfillmentSalesTaxAmount;
        a[id].totalDiscountAmount = (a[id].totalDiscountAmount || 0) + fulfillment.fulfillmentDiscountAmount;
        a[id].totalShippingAmount = (a[id].totalShippingAmount || 0) + thisShippingAmount;
        a[id].totalAmount = (a[id].totalAmount || 0) + fulfillment.fulfillmentSubTotalAmount - fulfillment.commissionAmount;
        a[id].marketPlaceCommission = (a[id].marketPlaceCommission || 0) + fulfillment.commissionAmount;
      }
      a[id].orderItems_on_order.push(...fulfillment.parcels_on_fulfillment.flatMap(parcel => parcel.orderItems_on_parcel).map(oi=>{
        const newOrderItem = {...oi}
        delete newOrderItem.totalTax;
        return newOrderItem;
      }))
      // FIXME! update fulfillment to remove tax and shipping and remove the marketplace fee (for the seller's view of refund data)
      fulfillment.refunds_on_fulfillment.forEach(refund => {
        if(refund.orderItems_on_refund){
          const itemsRefundAmount = refund.orderItems_on_refund.reduce((sum, item) => sum + (item.pricePerItemAtTimeOfOrder || 0) * (item.quantity || 1), 0)
          const amount = itemsRefundAmount // + (refund.shippingRefundAmount || 0);
          const commissionRefundAmount = Math.round(amount * commissionRate * 100) / 100;
          refund.amount = amount - commissionRefundAmount;
        }
        console.log({refund, fulfillment})
      })
      a[id].fulfillments_on_order.push(fulfillment)
      return a;
    }, {} as {[orderId: string]: OrderOrFulfillment})
    this.order = orderFromFulfillments[orderId];
    this.fulfillments = getFormattedFulfillments(fulfillments)
    console.log({order: this.order})
    this.order = this.order ? getFormattedOrder(this.order, 'fulfillment') : undefined;
    this.initComplete = true;
    this.startMarkingSeenInteractions();
    this.pageSetup$.next(); 
  }

  async startMarkingSeenInteractions(){
    if(this.startedMarkingSeenInteractions) return;
    this.startedMarkingSeenInteractions = true;
    if(!this.fulfillments.length) return;
    const fulfillmentIds = this.fulfillments.map(f=>f.id);
    this.store.select(interactionsFeature.selectEntities).pipe(debounceTime(800), takeUntil(this.$destroy)).subscribe((interactions) => {
      const seenInteractionIds: string[] = [];
      Object.values(interactions).forEach(interaction => {
        if(interaction 
          && !interaction.sellerViewed
          && interaction.fulfillment.id 
          && fulfillmentIds.includes(interaction.fulfillment.id)
        ){
          seenInteractionIds.push(interaction.id);
        }
      })
      console.log({seenInteractionIds, interactions})
      this.interactionsService.markInteractionsAsSeen(seenInteractionIds, this.location.id, "Seller");
    });
  }

  async setPageForManyOrders(){
    // FIXME: set up hydration monitor sync
    // this.store.select(ordersFeature.selectOrders).pipe(takeUntil(this.$destroy)).subscribe(orders => {
    //   this.orders = orders as OrderPublic[]
    // })
    // query orders!
    if(!this.location) this.location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), take(1))) as LocationPublic;
    const limit = 10
    const offset = 0;
    const orderVars: FulfillmentPageOrdersVariables = { locationId: this.location.id, limit, offset}
    log.log({orderVars})
    // first load from store,
    //this.fulfillments = await lastValueFrom(this.store.select(ordersFeature.selectOrders).pipe(take(1)))
    // then pull from db
    // other pulls should be as a result of filtering or sorting actions
    log.log({fulfillments: this.orders, areNotOrders: !this.orders.length})
    if(!this.orders.length){
      try {
        await sleep(100);
        const orderRef = await fulfillmentPageOrders(orderVars); 
        console.log({orderRef})
        const fulfillments = orderRef.data.fulfillments as unknown as Fulfillment[]
        log.log({ fulfillments});
        const ordersFromFulfillments = fulfillments.sort(sortByKey('fulfillmentNumber')).reduce((a, fulfillment, idx)=>{
          const {id, orderNumber, buyerId, orderPlaced, paymentTransactionId, statusId} = fulfillment.order;
          const marketPlaceCommission = fulfillment.commissionAmount;
          const totalAmount = fulfillment.fulfillmentSubTotalAmount - marketPlaceCommission;
          console.log({totalAmount})
          this.setReviews(fulfillment);
          if(!a[id]){
            const order: OrderPublic = {
              id,
              orderNumber,
              paymentTransactionId,
              buyerId,
              discounts_on_order: fulfillment.discounts_on_fulfillment,
              orderPlaced,
              fulfillments_on_order: [],
              orderItems_on_order: [],
              subTotalAmount: fulfillment.fulfillmentSubTotalAmount,
              totalTaxAmount: fulfillment.fulfillmentSalesTaxAmount,
              totalAmount,
              totalDiscountAmount: fulfillment.fulfillmentDiscountAmount,
              totalShippingAmount: fulfillment.rateCost,
              statusId,
              createdAt: fulfillment.createdAt,
            } 
            a[id] = order;
          }else{
            a[id].totalAmount = (a[id].totalAmount || 0) + totalAmount;
          }
          a[id].fulfillments_on_order.push(fulfillment)
          return a;
        }, {} as {[orderId: string]: OrderPublic})
        this.orders = Object.values(ordersFromFulfillments).map(order=>getFormattedOrder(order, "fulfillment"));
        console.log({orders: this.orders})
        this.pageSetup$.next(); 
        // this.store.dispatch(OrdersActions.ordersLoaded({orders}))
        // save to store as map
      } catch(error: any){
        log.error(error);
        this.snackBar.open(error.message, "close", {duration: 30 * 1000})
      }
    }
    this.orders.sort(sortByKey('createdAt',-1))
    this.initComplete = true;
  }

  setReviews(fulfillment: Fulfillment){
    if(fulfillment.reviews_on_fulfillment){
      fulfillment.reviews_on_fulfillment.forEach(review => {
        review.fulfillment = formatFulfillmentForReviewObject(fulfillment);
        if(review.isBuyer){ this.buyerReviews[fulfillment.id] = review; } 
        else { this.sellerReviews[fulfillment.id] = review; }
      })
    }
  }

  async openFulfillmentPanelToGetLabelOrEnterManualInfo(fulfillment: Fulfillment){
    const fulfillmentId: string = fulfillment.id;
    console.log({fulfillment})
    const shippingDialogData: StartShippingData = {
      fulfillment,
      rateId: fulfillment.rateId,
      rateCost: fulfillment.rateCost,
      trackingNumber: fulfillment.trackingNumber,
      trackingUrl: fulfillment.trackingUrl,
      shippingTransactionId: fulfillment.shippingTransactionId,
    }
    const dialog = this.dialog.open(StartShippingDialogComponent, {data: shippingDialogData, autoFocus: false})
    const response: StartShippingData | false = await lastValueFrom(dialog.afterClosed().pipe(take(1)))
    if(response){
      let {rateCost, trackingNumber, trackingUrl, messageToBuyer} = response
      const startFulfillmentShippingVars: StartFulfillmentShippingVariables = { 
        fulfillmentId, 
        locationId: this.location.id, 
        rateCost: fulfillment.rateCost * 100, 
        trackingNumber, 
        trackingUrl, 
        messageToBuyer};
      log.log({startFulfillmentShippingVars})
      await startFulfillmentShipping(startFulfillmentShippingVars);
      await this.notifyAboutFulfillmentChange(fulfillment, "shipment_started");
    }
  }

  async notifyAboutFulfillmentChange(fulfillment: Fulfillment, notificationHandle: NotificationHandle){
    const flagInteractionVars: FlagInteractionVariables = {
      idempotencyKey: `fulfillment_${fulfillment.id}_notification_${notificationHandle}`,
      locationIds: [ fulfillment.fulfilledByLocationId, fulfillment.fulfilledToLocationId ],
      fulfillmentId: fulfillment.id,
      audienceLocationId: fulfillment.fulfilledToLocationId,
      notificationHandle,
      orderId: fulfillment.order.id,
    };
    const callable = httpsCallable(this.functions,  onCallFunctions.flagInteractionCall);
    await callable(flagInteractionVars);
  }

  async actionTaken(fulfillment: Fulfillment){
    this.takingAction = true;
    let statusId: FulfillmentStatusType = fulfillment.statusId
    const fulfillmentId: string = fulfillment.id;
    if(!fulfillment.rateId && statusId === "required"){
      fulfillment.statusId =statusId = "fulfilling";
    }
    switch(statusId){
      case "required":
        console.log({fulfillment, fulfillments: this.fulfillments})
        if(fulfillment.rateId){
          const data: AcceptShippingRateDialogData ={ 
            rateId: fulfillment.rateId, 
            fulfillmentId,
            locationId: this.location.id,
            orderId: fulfillment.order.id,
            trackingNumber: fulfillment.trackingNumber,
            trackingUrl: fulfillment.trackingUrl,
            shipmentIsBeingReconfigured: fulfillment.shipmentIsBeingReconfigured,
            orderShippingAllocations: getOrderAllocatedShippingFunds(this.fulfillments, fulfillment.id),
            editedDimensions: fulfillment.parcels_on_fulfillment.map(parcel => ({
              id: parcel.id || uuidV4(),
              name: parcel.name,
              length: parcel.length || 0,
              width: parcel.width || 0,
              height: parcel.height || 0,
              distanceUnit: parcel.distanceUnit,
              weight: parcel.weight || 0,
              weightUnit: parcel.weightUnit
            }))
          }
          console.log({data})
          const dialog = this.dialog.open(AcceptShippingRateDialogComponent, {data})
          const results = await lastValueFrom(dialog.afterClosed().pipe(take(1)))
          console.log({results})
          if(results && 'action' in results && results.action === 'shipment_started'){
            // update statusId of fulfillment to "shipping" and rehydrate page
            // [ ] might be redundant to notify, the dialog also sends a flag on finalize, but doesn't send a notificationHandle. Look into adding it there and removing this notif call
            await startFulfillmentShipping({fulfillmentId, locationId: this.location.id} as StartFulfillmentShippingVariables);
            await this.notifyAboutFulfillmentChange(fulfillment, "shipment_started");
          }
          await this.fulfillmentConfigurationChanged(fulfillment);
        }
        break;
      case "fulfilling":
      case "shipping":
        await this.openFulfillmentPanelToGetLabelOrEnterManualInfo(fulfillment)
        break;
      case "downloadable":
      case "review":
        await this.openReviewDialog(this.sellerReviews[fulfillment.id], fulfillment)
        break;
    }
    console.log('resetting page')
    if(this.focusedOrderId){
      await this.setPageForOneOrder();
    } else {
      await this.setPageForManyOrders();
    }
    this.takingAction = false;
  }
  
  async openReviewDialog(review?: ReviewForEdit, fulfillment?: Fulfillment){
    if(!review && fulfillment){
      const id = uuidV4()
      review = createBlankReview(id, fulfillment, false)
    }
    if(!review) return;
    const dialogComponent = !review.isBuyer ? EditProductReviewDialogComponent : ViewProductReviewDialogComponent;
    const dialog = this.dialog.open(dialogComponent, {data: {review}});
    const updatedReview = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(updatedReview){
      const callable = httpsCallable(this.functions,  onCallFunctions.submitReview);
      try {
        const results: HttpsCallableResult = await uiErrorCatcher(callable({review: updatedReview}), this.snackBar, "Error submitting review") 
        console.log({results})
        // change fulfillment status to something
        this.snackBar.open("Review submitted successfully", "close", {duration: 3000});
      } catch(error){
        log.error("Error submitting review", error)
      }
    }
  }

  async fulfillmentConfigurationChanged(fulfillment: Fulfillment){
    console.log("fulfillment configuration changed, rehydrating page", fulfillment)
    if(!this.location) this.location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), take(1))) as LocationPublic;
    const fulfillmentRef = await getFulfillment({fulfillmentId: fulfillment.id, locationId: this.location.id})
    const fulfillments = fulfillmentRef.data.fulfillments as unknown as Fulfillment[]
    console.log({place: 'setPageForOneOrder', fulfillments})
    const formattedFulfillment = getFormattedFulfillments(fulfillments)[0];  
    this.fulfillments = this.fulfillments.map(f => f.id === fulfillment.id ? formattedFulfillment : f);
    this.pageSetup$.next(); 
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.pageSetup$.next();
    this.pageSetup$.complete();
  }

}
